/**
 * Consolidated Formatting Utilities
 * 
 * Single source of truth for all formatting functions across the application.
 * Optimized for performance and consistency.
 */

// Removed date-fns dependency - using native Intl API for better performance

// =============================================================================
// DATE AND TIME FORMATTING
// =============================================================================

/**
 * Formats a date based on organization preferences
 * @param date - Date to format
 * @param dateFormat - Date format preference
 * @returns Formatted date string
 */
export function formatDateByOrganization(
  date: Date | string | number,
  dateFormat: 'dd-mm-yyyy' | 'yyyy-mm-dd' = 'dd-mm-yyyy'
): string {
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();

  return dateFormat === 'dd-mm-yyyy' 
    ? `${day}-${month}-${year}`
    : `${year}-${month}-${day}`;
}

/**
 * Formats a date for display in lists and tables
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDisplayDate(date: Date | string): string {
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Formats a date and time for display
 * @param date - Date to format
 * @returns Formatted date and time string
 */
export function formatDateTime(date: Date | string): string {
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Validates and formats time string (HH:mm format)
 * @param time - Time string to validate and format
 * @returns Formatted time string or null if invalid
 */
export function formatTime(time: string): string | null {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
  const match = time.match(timeRegex);
  
  if (!match) return null;
  
  const hours = match[1].padStart(2, '0');
  const minutes = match[2];
  
  return `${hours}:${minutes}`;
}

/**
 * Formats duration in minutes to human-readable format
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}

// =============================================================================
// CURRENCY AND NUMBER FORMATTING
// =============================================================================

/**
 * Formats a currency amount with proper locale and currency symbol
 * Optimized version - single source of truth for all currency formatting
 * @param amount - The amount to format
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number, 
  currency: string = 'USD', 
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Formats a price with currency symbol only (no decimals for whole numbers)
 * @param amount - The amount to format
 * @param currency - Currency code (default: USD)
 * @returns Formatted price string
 */
export function formatPrice(amount: number, currency: string = 'USD'): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(amount);
}

/**
 * Formats a number with proper locale separators
 * Optimized version - single source of truth for all number formatting
 * @param num - Number to format
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted number string
 */
export function formatNumber(num: number, locale: string = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(num);
}

/**
 * Formats a percentage value
 * Optimized version - handles both value/total and direct percentage input
 * @param value - Value to format as percentage
 * @param total - Total value (optional, for calculating percentage)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, total?: number, decimals: number = 1): string {
  let percentage: number;
  
  if (total !== undefined) {
    // Calculate percentage from value/total
    percentage = total > 0 ? (value / total) * 100 : 0;
  } else {
    // Assume value is already a percentage (0-100)
    percentage = value;
  }
  
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Formats file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${size.toFixed(1)} ${sizes[i]}`;
}

// =============================================================================
// TEXT FORMATTING
// =============================================================================

/**
 * Formats a display name from first and last name
 * @param firstName - First name
 * @param lastName - Last name
 * @returns Formatted display name
 */
export function formatDisplayName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

/**
 * Formats a phone number for display
 * @param phoneNumber - Phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Handle different phone number lengths
  if (cleaned.length === 10) {
    // US format: (123) 456-7890
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US format with country code: +1 (123) 456-7890
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phoneNumber;
}

/**
 * Truncates text to a specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Capitalizes the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Converts a string to title case
 * @param str - String to convert
 * @returns Title case string
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
}

// =============================================================================
// ERROR FORMATTING
// =============================================================================

/**
 * Formats error messages for display
 * General purpose error formatter - use specific error formatters when available
 * @param error - Error to format
 * @returns Formatted error message
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    if (obj.message && typeof obj.message === 'string') {
      return obj.message;
    }
  }
  
  return 'An unknown error occurred';
}

// =============================================================================
// SAMPLE DATA UTILITIES
// =============================================================================

/**
 * Generates a random sample date within a range
 * @param start - Start date
 * @param end - End date
 * @returns Formatted random date string
 */
export function generateRandomSampleDate(start: Date, end: Date): string {
  const startTime = start.getTime();
  const endTime = end.getTime();
  const randomTime = startTime + Math.random() * (endTime - startTime);
  return formatDisplayDate(new Date(randomTime));
} 
